import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  LayoutDashboard, Package, Users, FileText,
  Eye, Boxes, Warehouse, Truck, Search, ClipboardList,
  UserCog, ChevronLeft, ChevronRight, LogOut, Menu,
  ChevronDown, ChevronUp, Scale, BarChart3, ShieldCheck, ClipboardCheck
} from 'lucide-react';

const NAV_SECTIONS = [
  {
    title: 'Principal',
    items: [
      { label: 'Panel', path: '/', icon: LayoutDashboard, roles: ['admin', 'encargada', 'supervisor', 'consulta'] },
    ]
  },
  {
    title: 'Catálogos',
    items: [
      { label: 'Repartidores', path: '/repartidores', icon: Users, roles: ['admin', 'encargada', 'supervisor'] },
      { label: 'Clientes', path: '/clientes', icon: Users, roles: ['admin', 'encargada', 'supervisor'] },
      { label: 'Grupos', path: '/grupos', icon: Package, roles: ['admin', 'encargada', 'supervisor', 'consulta'] },
      { label: 'Almacenes', path: '/almacenes', icon: Warehouse, roles: ['admin', 'supervisor', 'consulta'] },
    ]
  },
  {
    title: 'Operaciones',
    items: [
      { label: 'Captura de Vales', path: '/vales', icon: FileText, roles: ['admin', 'encargada'] },
      { label: 'Revisión', path: '/revision', icon: Eye, roles: ['admin', 'encargada', 'supervisor'] },
      { label: 'Garantía', path: '/garantia', icon: ShieldCheck, roles: ['admin', 'encargada'] },
      { label: 'Tarimas', path: '/tarimas', icon: Boxes, roles: ['admin', 'encargada'] },
      { label: 'Salida de Camión', path: '/embarques', icon: Truck, roles: ['admin', 'encargada'] },
    ]
  },
  {
    title: 'Consultas',
    items: [
      { label: 'Inventario', path: '/inventario', icon: Search, roles: ['admin', 'encargada', 'supervisor', 'consulta'] },
      { label: 'Bitácora', path: '/bitacora', icon: ClipboardList, roles: ['admin', 'supervisor', 'consulta'] },
      { label: 'Ajustes', path: '/ajustes', icon: Scale, roles: ['admin', 'supervisor'] },
      { label: 'Reportes', path: '/reportes', icon: BarChart3, roles: ['admin', 'supervisor', 'consulta'] },
      { label: 'Conteo Físico', path: '/conteo', icon: ClipboardCheck, roles: ['admin', 'encargada', 'supervisor'] },
    ]
  },
  {
    title: 'Sistema',
    items: [
      { label: 'Usuarios', path: '/usuarios', icon: UserCog, roles: ['admin'] },
    ]
  }
];

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const location = useLocation();

  const toggleSection = (title) => {
    setOpenSections(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const Sidebar = ({ mobile = false }) => (
    <div className={cn('flex flex-col h-full bg-[#F7F9FB] border-r border-[#DCE3EB]',
      mobile ? 'w-[280px]' : (collapsed ? 'w-[68px]' : 'w-[260px]'), 'transition-all duration-200')}
      data-testid="app-sidebar">
      <div className="flex items-center h-14 px-3 border-b border-[#DCE3EB] gap-2 shrink-0">
        <div className="h-8 w-8 rounded-lg bg-[#0F6E87] flex items-center justify-center shrink-0">
          <Warehouse className="h-4 w-4 text-white" />
        </div>
        {!collapsed && (<div className="min-w-0"><h1 className="text-sm font-semibold text-[#151A22] truncate">Bodega</h1><p className="text-[10px] text-muted-foreground">Control de Inventario</p></div>)}
        {!mobile && (<Button variant="ghost" size="icon" className="ml-auto h-7 w-7 shrink-0" onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-toggle-button">
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>)}
      </div>
      <ScrollArea className="flex-1 py-2">
        <nav className="space-y-1 px-2">
          {NAV_SECTIONS.map((section) => {
            const visibleItems = section.items.filter(item => item.roles.includes(user?.rol));
            if (visibleItems.length === 0) return null;
            const isOpen = openSections[section.title] !== false;
            return (
              <div key={section.title} className="mb-1">
                {!collapsed && (<button className="flex items-center justify-between w-full px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground" onClick={() => toggleSection(section.title)}>
                  {section.title}
                  {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </button>)}
                {(collapsed || isOpen) && visibleItems.map((item) => {
                  const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
                  return (
                    <NavLink key={item.path} to={item.path} onClick={() => mobile && setMobileOpen(false)}
                      className={cn('flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors duration-150',
                        isActive ? 'bg-[#0F6E87]/10 text-[#0F6E87] font-medium border-l-2 border-[#0F6E87]' : 'text-[#5E6A7A] hover:bg-muted hover:text-foreground',
                        collapsed && 'justify-center px-0')}
                      data-testid={`sidebar-nav-item-${item.path.replace('/', '') || 'dashboard'}`}>
                      <item.icon className={cn('h-4 w-4 shrink-0', isActive && 'text-[#0F6E87]')} />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </ScrollArea>
      <div className="border-t border-[#DCE3EB] p-3 shrink-0">
        <div className={cn('flex items-center gap-2', collapsed && 'justify-center')}>
          <div className="h-8 w-8 rounded-full bg-[#0F6E87]/10 flex items-center justify-center shrink-0">
            <span className="text-xs font-semibold text-[#0F6E87]">{user?.nombre?.charAt(0)?.toUpperCase() || 'U'}</span>
          </div>
          {!collapsed && (<div className="min-w-0 flex-1"><p className="text-xs font-medium truncate">{user?.nombre}</p><p className="text-[10px] text-muted-foreground capitalize">{user?.rol}</p></div>)}
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={logout} data-testid="logout-button">
            <LogOut className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#F7F9FB] overflow-hidden">
      <div className="hidden md:flex"><Sidebar /></div>
      {mobileOpen && (<div className="fixed inset-0 z-50 md:hidden">
        <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
        <div className="relative z-10 h-full"><Sidebar mobile /></div>
      </div>)}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-14 border-b border-[#DCE3EB] bg-white flex items-center px-4 gap-3 shrink-0">
          <Button variant="ghost" size="icon" className="md:hidden h-8 w-8" onClick={() => setMobileOpen(true)}><Menu className="h-4 w-4" /></Button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground hidden sm:inline">{user?.nombre}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#0F6E87]/10 text-[#0F6E87] font-medium capitalize" data-testid="current-role-pill">{user?.rol}</span>
            <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground hover:text-red-600" onClick={logout} data-testid="topbar-logout-button">
              <LogOut className="h-3.5 w-3.5 mr-1" /> Salir
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-5">{children}</div>
        </main>
      </div>
    </div>
  );
}
